import React from 'react';

import type { Node } from './../../ricos-schema/legacy/wix/rich_content/v1';
import { Decoration_Type } from './../../ricos-schema/legacy/wix/rich_content/v1';
import type { MentionDecoration, NodeViewDecorator, ViewerContextualData } from './../../ricos-types';

import styles from '../statics/mentions-scss';
import { MENTION_TYPE } from '../types';

const decorator = (
  element: JSX.Element,
  mentionDecoration: MentionDecoration,
  contextualData: ViewerContextualData
) => {
  const { mentionData } = mentionDecoration;
  const settings = contextualData.config[MENTION_TYPE] || {};
  const { onMentionClick, onMentionHover, getMentionLink } = settings;

  const ref = React.useRef(null);

  const onMouseOver = () => onMentionHover?.(mentionData, ref.current);
  const onClick = () => onMentionClick?.(mentionData);
  const onKeyDown = (e: $TSFixMe) => (e.key === 'Enter' || e.key === ' ') && onClick();
  const cursorStyle = onMentionClick === undefined ? styles.cursorDefault : styles.cursorPointer;

  const commonProps = {
    onMouseOver,
    ref,
    className: `${styles.mention} ${styles.mentionViewer} ${cursorStyle}`,
  };

  const key = `mention-${mentionData.id}`;

  if (onMentionClick) {
    return (
      <span
        key={key}
        {...commonProps}
        role="link"
        tabIndex={0}
        onKeyDown={onKeyDown}
        onClick={onClick}
      >
        {element}
      </span>
    );
  } else if (getMentionLink) {
    return (
      <a
        key={key}
        {...commonProps}
        href={getMentionLink(mentionData)}
        rel="noopener noreferrer"
        tabIndex={0}
      >
        {element}
      </a>
    );
  } else {
    return (
      <span key={key} {...commonProps}>
        {element}
      </span>
    );
  }
};

const MentionDecorator: NodeViewDecorator<JSX.Element> = {
  rule: (node: Node) =>
    !!node.textData?.decorations?.some(({ type }) => type === Decoration_Type.MENTION),
  decorate: ({ element, node, context }) => {
    const mentionDecoration = node.textData?.decorations?.find(
      ({ type }) => type === Decoration_Type.MENTION
    ) as MentionDecoration;
    return decorator(element as JSX.Element, mentionDecoration, context);
  },
};

export default MentionDecorator;
